import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Send, CheckCircle2, Store, Users, Sparkles } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none transition-colors focus:border-amber-400/70";

const CATEGORIES = [
  "Food & Drink",
  "Benessere & Beauty",
  "Shopping & Moda",
  "Esperienze & Tempo libero",
  "Casa & Regali",
  "Altro",
];

const PERKS = [
  { icon: Users, title: "Nuovi clienti", text: "La tua offerta arriva nelle mani di migliaia di persone, una casella alla volta." },
  { icon: Store, title: "Zero costi", text: "Partecipare è gratuito: offri il vantaggio, al resto pensiamo noi." },
  { icon: Sparkles, title: "Visibilità premium", text: "Il tuo brand compare nel calendario, nelle email e sul sito." },
];

const initialForm = {
  company_name: "",
  website: "",
  category: "",
  city: "",
  offer: "",
  normal_value: "",
  conditions: "",
  max_vouchers: "",
  expires_on: "",
  contact_name: "",
  phone: "",
  email: "",
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-emerald-100/60">
      {label} {required && <span className="text-amber-300">*</span>}
    </span>
    {children}
  </label>
);

export default function PartnerProposal() {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/brand-proposals", {
        ...form,
        max_vouchers: form.max_vouchers ? parseInt(form.max_vouchers, 10) : null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err, "Invio non riuscito. Controlla i campi e riprova."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#06150F] text-[#FAF6EE]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,rgba(90,15,25,0.55),transparent_65%)]"
      />
      <SiteHeader />
      <main className="relative z-10 mx-auto max-w-3xl px-5 pb-20 pt-14 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/90">Diventa partner</p>
          <h1 className="font-display mt-3 text-3xl font-bold leading-tight text-amber-100 sm:text-4xl">
            Porta il tuo brand sotto l'albero di migliaia di italiani.
          </h1>
          <p className="mt-4 text-base font-semibold text-amber-200/90 sm:text-lg">Partecipare è gratuito.</p>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-emerald-100/70 sm:text-base">
            Offri un vantaggio esclusivo e raggiungi nuovi potenziali clienti.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 grid gap-4 sm:grid-cols-3"
        >
          {PERKS.map((perk) => (
            <div
              key={perk.title}
              data-testid={`partner-perk-${perk.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/60 p-5 text-center"
            >
              <perk.icon className="mx-auto h-5 w-5 text-amber-300" />
              <p className="mt-2.5 text-sm font-bold text-amber-100">{perk.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-emerald-100/65">{perk.text}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-10 rounded-3xl border border-amber-500/25 bg-[#0A1D15]/80 p-7 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-10"
          data-testid="partner-card"
        >
          {done ? (
            <div className="py-6 text-center" data-testid="partner-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-300" />
              <h2 className="font-display mt-4 text-2xl font-bold text-amber-100">Proposta inviata!</h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-emerald-100/75">
                Grazie! Il nostro team valuterà la tua proposta: riceverai un'email quando sarà
                approvata, insieme a eventuali note. Di solito rispondiamo entro pochi giorni.
              </p>
              <Link
                to="/"
                data-testid="partner-back-home"
                className="mt-7 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-7 py-3.5 font-bold text-[#1A1204] transition-transform hover:scale-[1.03]"
              >
                Torna alla home
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-400/15">
                  <Gift className="h-5 w-5 text-amber-300" />
                </span>
                <div>
                  <h2 className="font-display text-xl font-bold text-amber-100">La tua proposta</h2>
                  <p className="text-xs text-emerald-100/60">Compila il modulo: ti risponderemo via email.</p>
                </div>
              </div>
              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="partner-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Nome azienda" required>
                    <input data-testid="partner-company-input" required minLength={2} value={form.company_name} onChange={set("company_name")} placeholder="Es. Pasticceria Dolce Neve" className={inputCls} />
                  </Field>
                  <Field label="Sito web">
                    <input data-testid="partner-website-input" value={form.website} onChange={set("website")} placeholder="www.tuobrand.it" className={inputCls} />
                  </Field>
                  <Field label="Categoria" required>
                    <select data-testid="partner-category-select" required value={form.category} onChange={set("category")} className={inputCls}>
                      <option value="" disabled>Seleziona una categoria</option>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Città" required>
                    <input data-testid="partner-city-input" required minLength={2} value={form.city} onChange={set("city")} placeholder="Es. Milano" className={inputCls} />
                  </Field>
                </div>
                <Field label="Offerta proposta" required>
                  <textarea data-testid="partner-offer-input" required minLength={5} rows={3} value={form.offer} onChange={set("offer")} placeholder="Es. Un panettone artigianale in omaggio con una spesa minima di 20€" className={`${inputCls} resize-none`} />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Valore normale (€)">
                    <input data-testid="partner-value-input" value={form.normal_value} onChange={set("normal_value")} placeholder="Es. 25" className={inputCls} />
                  </Field>
                  <Field label="Numero massimo voucher">
                    <input data-testid="partner-vouchers-input" type="number" min="1" value={form.max_vouchers} onChange={set("max_vouchers")} placeholder="Es. 200" className={inputCls} />
                  </Field>
                </div>
                <Field label="Condizioni">
                  <textarea data-testid="partner-conditions-input" rows={2} value={form.conditions} onChange={set("conditions")} placeholder="Es. Valido dal lunedì al venerdì, non cumulabile con altre promo" className={`${inputCls} resize-none`} />
                </Field>
                <Field label="Scadenza offerta">
                  <input data-testid="partner-expiry-input" type="date" value={form.expires_on} onChange={set("expires_on")} className={inputCls} />
                </Field>
                <div className="grid gap-5 border-t border-amber-500/15 pt-5 sm:grid-cols-3">
                  <Field label="Referente" required>
                    <input data-testid="partner-contact-input" required minLength={2} value={form.contact_name} onChange={set("contact_name")} placeholder="Nome e cognome" className={inputCls} />
                  </Field>
                  <Field label="Telefono" required>
                    <input data-testid="partner-phone-input" type="tel" required minLength={5} value={form.phone} onChange={set("phone")} placeholder="+39 ..." className={inputCls} />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="partner-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="partner@tuobrand.it" className={inputCls} />
                  </Field>
                </div>
                {error && (
                  <p data-testid="partner-error" className="rounded-lg border border-red-400/40 bg-red-900/20 px-4 py-2.5 text-sm text-red-200">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="partner-submit-button"
                  disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-4 font-bold uppercase tracking-wider text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/30 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" /> {busy ? "Invio in corso..." : "Invia la tua proposta"}
                </button>
                <p className="text-center text-xs text-emerald-100/50">
                  Inviando accetti di essere ricontattato via email o telefono in merito alla proposta.
                </p>
              </form>
            </>
          )}
        </motion.div>
      </main>
      <SiteFooter />
    </div>
  );
}
